
import { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";

import { useTheme } from "@mui/material/styles";

import BoltIcon from "@mui/icons-material/Bolt";
import SpeedIcon from "@mui/icons-material/Speed";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";

import { sendDataToServer } from "utils/functions";

const PERIODS = [
  { value: "hour", title: "Часовой" },
  { value: "day", title: "Суточный" },
  { value: "week", title: "Недельный" },
  { value: "month", title: "Месячный" },
  { value: "year", title: "Годовой" },
];

function formatTime(unix, period) {
  const date = new Date(unix * 1000);

  if (period === "hour" || period === "day") {
    return date.toLocaleTimeString("ru-RU", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  if (period === "week") {
    return date.toLocaleDateString("ru-RU", {
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return date.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
  });
}

function validNumber(value) {
  return value !== null &&
    value !== undefined &&
    value !== "" &&
    Number.isFinite(Number(value));
}

function latestValue(data, field) {
  for (let i = data.length - 1; i >= 0; i--) {
    if (validNumber(data[i][field])) {
      return Number(data[i][field]);
    }
  }
  return null;
}

const VOLTAGE_SERIES = [
  {
    field: "input_voltage",
    label: "Вход",
    color: "#1976d2",
    axis: "left",
  },
  {
    field: "output_voltage",
    label: "Выход",
    color: "#2e7d32",
    axis: "left",
  },
  {
    field: "battery_voltage",
    label: "АКБ",
    color: "#ed6c02",
    axis: "right",
  },
];

function PowerChart({
  title,
  icon,
  data,
  series,
  period,
  suffix,
  digits = 1,
}) {
  const theme = useTheme();

  const width = 1000;
  const height = 310;

  const left = 72;
  const right = series.some((s) => s.axis === "right")
    ? 75
    : 25;
  const top = 25;
  const bottom = 60;

  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;

  const prepared = useMemo(() => {
    return series.map((s) => ({
      ...s,
      points: data
        .filter((row) => validNumber(row[s.field]))
        .map((row) => ({
          time: Number(row.time),
          value: Number(row[s.field]),
        })),
    }));
  }, [data, series]);

  const allPoints = prepared.flatMap((s) => s.points);

  if (!allPoints.length) {
    return (
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="h6">{title}</Typography>
        <Typography color="text.secondary">
          Нет измерений
        </Typography>
      </Paper>
    );
  }

  const minTime = Math.min(...allPoints.map((p) => p.time));
  const maxTime = Math.max(...allPoints.map((p) => p.time));
  const timeDelta = Math.max(1, maxTime - minTime);

  function getRange(axis) {
    const values = prepared
      .filter((s) => (s.axis || "left") === axis)
      .flatMap((s) => s.points.map((p) => p.value));

    if (!values.length) return { min: 0, max: 1 };

    let min = Math.min(...values);
    let max = Math.max(...values);

    const delta = max - min || Math.max(1, Math.abs(max) * 0.02);
    const padding = delta * 0.1;

    min -= padding;
    max += padding;

    return { min, max };
  }

  const leftRange = getRange("left");
  const rightRange = getRange("right");

  const x = (time) =>
    left + ((time - minTime) / timeDelta) * plotWidth;

  const y = (value, axis = "left") => {
    const range = axis === "right" ? rightRange : leftRange;

    return (
      top +
      (1 - (value - range.min) / (range.max - range.min)) *
        plotHeight
    );
  };

  const horizontalLines = Array.from(
    { length: 5 },
    (_, i) => i / 4
  );

  const verticalLines = Array.from(
    { length: 5 },
    (_, i) => i / 4
  );

  const latest = prepared.map((s) => ({
    ...s,
    value: latestValue(data, s.field),
  }));

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2 },
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {icon}
          <Typography variant="h6">{title}</Typography>
        </Stack>

        <Stack direction="row" flexWrap="wrap" gap={2}>
          {latest.map((s) => (
            <Stack
              key={s.field}
              direction="row"
              alignItems="center"
              spacing={0.7}
            >
              <Box
                sx={{
                  width: 12,
                  height: 4,
                  bgcolor: s.color,
                  borderRadius: 1,
                }}
              />
              <Typography variant="body2">
                {s.label}:{" "}
                <strong>
                  {s.value === null
                    ? "—"
                    : s.value.toFixed(digits)}
                  {suffix}
                </strong>
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>

      <Box sx={{ width: "100%", overflow: "hidden" }}>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          style={{ display: "block", width: "100%", height: "auto" }}
        >
          {horizontalLines.map((ratio, i) => {
            const yy = top + ratio * plotHeight;

            const lv =
              leftRange.max -
              ratio * (leftRange.max - leftRange.min);

            const rv =
              rightRange.max -
              ratio * (rightRange.max - rightRange.min);

            return (
              <g key={`h-${i}`}>
                <line
                  x1={left}
                  y1={yy}
                  x2={width - right}
                  y2={yy}
                  stroke={theme.palette.divider}
                />

                <text
                  x={left - 9}
                  y={yy + 4}
                  textAnchor="end"
                  fontSize="13"
                  fill={theme.palette.text.secondary}
                >
                  {lv.toFixed(digits)}
                </text>

                {series.some((s) => s.axis === "right") && (
                  <text
                    x={width - right + 9}
                    y={yy + 4}
                    textAnchor="start"
                    fontSize="13"
                    fill="#ed6c02"
                  >
                    {rv.toFixed(digits)}
                  </text>
                )}
              </g>
            );
          })}

          {verticalLines.map((ratio, i) => {
            const xx = left + ratio * plotWidth;
            const timestamp = minTime + ratio * timeDelta;

            return (
              <g key={`v-${i}`}>
                <line
                  x1={xx}
                  y1={top}
                  x2={xx}
                  y2={top + plotHeight}
                  stroke={theme.palette.divider}
                />
                <text
                  x={xx}
                  y={height - 25}
                  textAnchor={
                    i === 0 ? "start" :
                    i === 4 ? "end" : "middle"
                  }
                  fontSize="13"
                  fill={theme.palette.text.secondary}
                >
                  {formatTime(timestamp, period)}
                </text>
              </g>
            );
          })}

          {prepared.map((s) => {
            const axis = s.axis || "left";

            // Не соединяем пропуски длиннее трёх
            // стандартных интервалов для периода.
            const bucketSeconds = {
              hour: 60,
              day: 300,
              week: 900,
              month: 3600,
              year: 21600,
            }[period] || 60;

            const segments = [];
            let segment = [];

            s.points.forEach((point) => {
              const prev = segment[segment.length - 1];

              if (
                prev &&
                point.time - prev.time > bucketSeconds * 3
              ) {
                segments.push(segment);
                segment = [];
              }

              segment.push(point);
            });

            if (segment.length) segments.push(segment);

            return (
              <g key={s.field}>
                {segments.map((points, index) => {
                  if (points.length === 1) {
                    return (
                      <circle
                        key={index}
                        cx={x(points[0].time)}
                        cy={y(points[0].value, axis)}
                        r="4"
                        fill={s.color}
                      />
                    );
                  }

                  return (
                    <polyline
                      key={index}
                      points={points
                        .map((p) =>
                          `${x(p.time)},${y(p.value, axis)}`
                        )
                        .join(" ")}
                      fill="none"
                      stroke={s.color}
                      strokeWidth="2.5"
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    />
                  );
                })}
              </g>
            );
          })}
        </svg>
      </Box>
    </Paper>
  );
}

export default function PowerPage() {
  const [period, setPeriod] = useState("day");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        const response = await sendDataToServer({
          op: "guestGetPowerChart",
          period,
        });

        if (!active) return;

        if (!response || response.status !== "OK") {
          throw new Error(
            response?.error || "Не удалось получить данные ИБП"
          );
        }

        setData(
          Array.isArray(response.points) ? response.points : []
        );
        setError("");
      } catch (err) {
        if (!active) return;
        setError(err.message || "Ошибка получения данных");
        setData([]);
      } finally {
        if (active) setLoading(false);
      }
    }

    setLoading(true);
    load();

    const timer = setInterval(load, 60000);

    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [period]);

  const chartData = data;

  return (
    <Box sx={{ py: { xs: 3, sm: 5 } }}>
      <Typography
        variant="h4"
        sx={{ mb: 3, fontSize: { xs: "1.7rem", sm: "2rem" } }}
      >
        Электропитание ГСТВ
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <FormControl fullWidth>
          <InputLabel>Интервал</InputLabel>
          <Select
            value={period}
            label="Интервал"
            onChange={(event) => setPeriod(event.target.value)}
          >
            {PERIODS.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && !chartData.length && (
        <Alert severity="info">
          За выбранный период данных нет.
        </Alert>
      )}

      {!loading && !error && chartData.length > 0 && (
        <Stack spacing={2}>
          <PowerChart
            title="Напряжение"
            icon={<BoltIcon />}
            data={chartData}
            series={VOLTAGE_SERIES}
            period={period}
            suffix=" В"
          />

          <PowerChart
            title="Частота сети"
            icon={<SpeedIcon />}
            data={chartData}
            series={[
              {
                field: "input_frequency",
                label: "Частота",
                color: "#1976d2",
              },
            ]}
            period={period}
            suffix=" Гц"
          />

          <PowerChart
            title="Потребляемая мощность ГСТВ"
            icon={<ElectricBoltIcon />}
            data={chartData}
            series={[
              {
                field: "power_watts",
                label: "Мощность",
                color: "#ed6c02",
              },
            ]}
            period={period}
            suffix=" Вт"
            digits={0}
          />
        </Stack>
      )}
    </Box>
  );
}
